import React, { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { Ship, Truck, Plane, Globe, Menu, X, ChevronRight, ShieldCheck } from 'lucide-react';
import { ROUTE_PATHS } from '@/lib/index';
import { Button } from '@/components/ui/button';
interface LayoutProps {
  children: React.ReactNode;
}
export function Layout({
  children
}: LayoutProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(64);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    const updateHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };
    window.addEventListener('scroll', handleScroll);
    const resizeObserver = new ResizeObserver(updateHeight);
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }
    updateHeight();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, []);
  const navItems = [{
    label: 'Home',
    path: ROUTE_PATHS.HOME,
    icon: Globe
  }];
  return <div className="min-h-screen bg-background flex flex-col font-sans">
      {/* Navigation Header */}
      <header ref={headerRef} className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b ${isScrolled ? 'bg-background/80 backdrop-blur-md border-border shadow-sm py-2' : 'bg-background border-transparent py-4'}`}>
        <div className="w-full px-6 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <NavLink to={ROUTE_PATHS.HOME} className="flex items-center gap-2.5 group transition-transform active:scale-95">
              <div className="bg-primary p-2 rounded-lg text-primary-foreground">
                <Ship size={22} className="group-hover:rotate-12 transition-transform duration-300" />
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-bold tracking-tight text-foreground leading-none">
                  Logistics Intelligence
                </span>
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mt-1">
                  Strategic Market Portal
                </span>
              </div>
            </NavLink>

            <nav className="hidden md:flex items-center gap-1">
              {navItems.map(item => <NavLink key={item.path} to={item.path} className={({
              isActive
            }) => `flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${isActive ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'}`}>
                  <item.icon size={16} />
                  {item.label}
                </NavLink>)}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            


            <button className="md:hidden p-2 text-foreground hover:bg-secondary rounded-lg transition-colors" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && <div className="md:hidden absolute top-full left-0 right-0 bg-background border-b border-border p-4 animate-in slide-in-from-top-4 duration-200">
            <nav className="flex flex-col gap-2">
              {navItems.map(item => <NavLink key={item.path} to={item.path} onClick={() => setIsMobileMenuOpen(false)} className={({
            isActive
          }) => `flex items-center gap-3 p-3 rounded-xl text-sm font-medium ${isActive ? 'bg-primary text-primary-foreground' : 'hover:bg-secondary text-muted-foreground'}`}>
                  <item.icon size={18} />
                  {item.label}
                </NavLink>)}
            </nav>
          </div>}
      </header>

      {/* Main Content Area */}
      <main className="flex-grow flex flex-col" style={{
      paddingTop: `${headerHeight}px`
    }}>
        <div className="w-full flex-grow">
          {children}
        </div>
      </main>

      {/* Global Footer */}
      <footer className="bg-card border-t border-border py-12">
        <div className="w-full px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="bg-primary/10 p-1.5 rounded-md">
                  <Globe size={18} className="text-primary" />
                </div>
                <span className="font-bold text-foreground">Logistics Intelligence</span>
              </div>
              <p className="text-sm text-muted-foreground max-w-xs leading-relaxed">
                Real-time monitoring of global supply chain dynamics, providing in-depth data support and strategic insights for the complex European logistics market in 2026.
              </p>
            </div>

            

            <div className="space-y-4">
              
              <div className="flex flex-col gap-2">
                
                <div className="text-sm text-muted-foreground">
                  Last Updated: February 13, 2026 07:50
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-border flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-xs text-muted-foreground">
              © 2026 Strategic Logistics Intelligence | For Internal Use Only
            </p>
            <div className="flex items-center gap-6">
              
              
              
            </div>
          </div>
        </div>
      </footer>
    </div>;
}